import { useCallback } from 'react';
import { RESET } from 'jotai/utils';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from 'services/analytics/trackEvent';
import UserAPI from 'services/API/UserAPI';
import { useUser } from 'state/auth';
import { useClearGroup } from 'state/dashboard';
import { useUserPermittedCities } from 'state/userPermissions';
import { trackLogin } from 'utils/analytics';
import { LoginSuccessStates } from 'utils/analytics/constants';
import type { UserResponse } from 'types/user';

/**
 * Hook to manage user session within the app.
 */
export const useUserSession = () => {
  const { setToken, setUser, setRefreshToken } = useUser();
  const [, setUserPermittedCities] = useUserPermittedCities();
  const { clearGroup } = useClearGroup();
  const navigate = useNavigate();

  const initializeUserSession = useCallback(
    async (accessToken: string, refreshToken: string) => {
      setToken(accessToken);
      setRefreshToken(refreshToken);

      /**
       * Ensure this is a valid token before proceeding.
       * We may have logged in with SSO, but we don't have a Mercator user
       * associated with it.
       */
      await UserAPI.validateToken();

      const { data } = await UserAPI.getUserData();
      const userData: UserResponse = data.data;
      setUser({
        email: userData.email,
        id: userData.id,
        first_name: userData.first_name,
        last_name: userData.last_name,
        company_name: userData.company.name,
        company_id: userData.company.uuid,
        company_billing_cycle: userData.company.billing_cycle,
        company_website: userData.company.website,
        profile_id: userData.profile_id,
        is_email_verified: userData.is_email_verified,
        view_preferences: userData.view_preferences,
      });
      setUserPermittedCities(userData.city_permissions.map(({ city_id }) => city_id));
      trackLogin({
        userId: userData.id,
        companyId: userData.company.uuid,
        successState: LoginSuccessStates.SUCCESS,
      });
    },
    [setUser, setToken, setUserPermittedCities, setToken, setRefreshToken, trackEvent],
  );

  const resetUserSession = useCallback(() => {
    setUser(RESET);
    setToken(RESET);
    setRefreshToken(RESET);
    clearGroup();
  }, [setToken, setUser, navigate]);

  return { initializeUserSession, resetUserSession };
};
